import { FC } from 'react';
import { Button } from '@carbon/react';
import { Settings } from '@carbon/react/icons';
import styles from '../allocation-manager.module.scss';
import { AllocationManagerSourceDocumentModal } from './allocation-manager-source-document-modal';
import { AllocationManagerResponse } from 'types/api-responses';
import { VersionSwitcher } from 'components/allocation-manager/version-switcher';
import { VerificationControls } from 'components/verification-controls';

interface Props {
  allocationManager: AllocationManagerResponse;
  wellData: any;
  versions: string[];
  selectedVersion: string;
  onVersionChange?: (version: string) => void;
  onAssignDocument?: (isRemove: boolean) => Promise<void>;
  supportingFilesHref: string;
  latestAllocationManagerVersion: string | null;
  developmentAreaId?: string;
  viewOnlySourceDocument?: boolean;
  skipModal?: boolean;
  disableActions?: boolean;
  onOpenAllocationMethodModal?: () => void;
  hasAllocationManager: boolean;
  hasLateralLengthAllocation?: boolean;
}

export const AllocationManagerHeaderActions: FC<Props> = ({
  allocationManager,
  wellData,
  versions,
  selectedVersion,
  onVersionChange,
  onAssignDocument,
  supportingFilesHref,
  latestAllocationManagerVersion,
  developmentAreaId,
  viewOnlySourceDocument = false,
  skipModal = false,
  disableActions = false,
  onOpenAllocationMethodModal,
  hasAllocationManager = false,
  hasLateralLengthAllocation = false,
}) => {
  return (
    <div className={styles.headerActions}>
      {versions && versions.length > 0 && (
        <VersionSwitcher
          items={versions}
          selectedItem={selectedVersion}
          onChange={(version) => onVersionChange?.(version)}
          getItemKey={(version) => version}
          renderItemLabel={(version: string) => (
            <span>
              Version {version}
              {version === selectedVersion ? ' (Current)' : ''}
            </span>
          )}
          menuClassName="versionSwitcherMenu"
          className={styles.versionSelector}
        />
      )}
      <div className={styles.rightActions}>
        {developmentAreaId &&
          allocationManager.verifications?.allocationMethods?.href && (
            <VerificationControls
              eventsHref={
                allocationManager.verifications.allocationMethods.href.events
              }
              commentsHref={
                allocationManager.verifications.allocationMethods.href.comments
              }
              displayName="Allocation Manager"
              initialStatus={
                allocationManager.verifications.allocationMethods.status
              }
              initialCommentsCount={
                allocationManager.verifications.allocationMethods.commentsCount
              }
              skipModal={skipModal}
              disableActions={disableActions}
            />
          )}
        <div className={styles.sourceDocumentContainer}>
          <AllocationManagerSourceDocumentModal
            allocationManagerId={allocationManager.id}
            hasSourceDocument={!!allocationManager.supportingFile}
            onAssignDocument={onAssignDocument}
            supportingFilesHref={supportingFilesHref}
            wellData={wellData}
            allocationManager={allocationManager}
            selectedVersion={selectedVersion}
            latestAllocationManagerVersion={latestAllocationManagerVersion}
            viewOnly={viewOnlySourceDocument}
          />
        </div>
        {hasAllocationManager && (
          <Button
            renderIcon={Settings}
            onClick={onOpenAllocationMethodModal}
            size="md"
            disabled={
              !onOpenAllocationMethodModal || !hasLateralLengthAllocation
            }
          >
            <span>Configure Unit Allocation Method</span>
          </Button>
        )}
      </div>
    </div>
  );
};
